import {
  Award, CheckCircle2, EllipsisVerticalIcon, Eye, Target, TrendingUp, Users
} from 'lucide-react';
import useUserAccount from '../../../../hooks/userAccountHook';
import { StatsCard } from './components/StatsCard';
import SalesTrendsChart from './components/SalesTrendsChart';
import InsuranceCompanyDistribution from './components/InsuranceCompanyDistribution';
import {
  getFormattedDate,
  getGreeting,
  getPhoneNumberFromShortCodeFormat
} from '../../../../utils/miscFunctions';
import { formatCurrencyAmount } from '../../../../utils/miscFunctions';
import { useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import useModal from '../../../../hooks/modalHook';
import { Link, useNavigate } from 'react-router-dom';
import useRoute from '../../../../hooks/routesHook';
import GTable from '../../../../components/elements/GTable';
import useBroker from '../../../../hooks/brokerHook';
import { useDispatch, useSelector } from 'react-redux';
import { getSelectedCountry } from '../../../../states/slices/resourceSlice';
import usePolicy from '../../../../hooks/policyHook';
import moment from 'moment';
import SelectMenu from '../../../../components/form-control/SelectMenu';
import PolicyDistribution from './components/PolicyDistribution';
import RenewalDistributionChart from './components/RenewalDistributionChart';
import { IPolicyRenewal } from '../../policies/auto/Renewals';
import { ITableRowAction } from '../../../../components/elements/DataTable';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { GTableActionColumn } from '../../../../components/elements/GTable/GTable';
import usePartner from '../../../../hooks/partnerHook';
import { SET_COMPANY_ONBOARDING_STATUS, SET_PENDING_ACTIONS, getCompanyOnboardingStatusFromState, getWorkspaceSetupBannerMinimized } from '../../../../states/slices/globalSlice';
import Tooltip from '../../../../components/ToolTip';
import PopOnMe from '../../../../components/elements/poponme';
import WorkspaceSetupBanner from '../../../../components/ui/WorkspaceSetupBanner';
import styles from './BrokerDashboard.module.css';


interface BrokerDashboardProps {
  companyId?: number;
}

const BrokerDashboard = ({ companyId }: BrokerDashboardProps = {}) => {
  const isAdminEmbed = companyId !== undefined;
  const { user } = useUserAccount();
  const { ModalComponent } = useModal();
  const navigate = useNavigate();
  const { getDashboardBase } = useRoute();
  const queryClient = useQueryClient()
  const [metricTitle, setMetricTitle] = useState<string>('Financial Metrics')
  const [activeMetricType, setActiveMetricType] = useState<'alerts' | 'financial' | 'customer'>('financial');
  const { PolicyModalComponent, PolicyTypesBadge, getAllExpiringPolicies, renewMotorPolicy, markAsRenewed } = usePolicy();
  const {
    getDashboardChartMonthlyMetrics,
    getBrokerDashboardMetricsSummaryData,
    getInsuranceMetrics,
    getRevenueTrendData,
    brokerMetricsSummaryData,
    isLoading,
    monthlyChartMetrics,
    BrokerModal,
    // handleCreateRevenueTarget,
    // getBrokerDashboardCustomerMetrics,
    // getRevenueTargetData,
    // setTargetPeriod,
    // insuranceMetrics,
    // customerMetricsData,
    // revenueTargetData,
    // targetPeriod,
    // revenueTarget
  } = useBroker();
  const targetAchievementRef = useRef<HTMLDivElement>(null);
  const months: string[] = monthlyChartMetrics && monthlyChartMetrics?.months;
  const revenues = monthlyChartMetrics && monthlyChartMetrics?.salesAmounts;
  const targets = monthlyChartMetrics && monthlyChartMetrics?.revenueTargets;
  const revenueChartData = months ? months?.map((month: string, index) => ({
    month,
    revenue: revenues[index],
    target: targets[index]
  })) : [];
  const country = useSelector(getSelectedCountry)
  const dispatch = useDispatch();
  const isMinimized = useSelector(getWorkspaceSetupBannerMinimized);
  const onboardingStatus = useSelector(getCompanyOnboardingStatusFromState);
  const { fetchCompanyOnboardingStatus } = usePartner();

  const isOnboardingIncomplete = onboardingStatus?.onboardingComplete === false;
  const shouldShowBannerSidebar = isOnboardingIncomplete && !isMinimized;

  const inavalidateRenewals = () => { queryClient.invalidateQueries({ queryKey: ['getAllExpiringPolicies'] }) }

  const getCompanyOnboardingStatus = async () => {

    try {
      const data = await fetchCompanyOnboardingStatus();
      if (data) {
        dispatch(SET_PENDING_ACTIONS(data?.pendingActions));
        dispatch(SET_COMPANY_ONBOARDING_STATUS(data))
      }
    } catch (error) {

    }
  }

  const getCurrentYear = () => {
    const currentYear = new Date();
    return moment(currentYear).format('YYYY');
  }

  const [fieldValue, setFieldValue] = useState<string>(getCurrentYear())

  const getCurrentMonth = () => {
    const currentMonth = new Date();
    return moment(currentMonth).format('MMM');
  }

  // useQuery({
  //   queryKey: ['getRevenueTargetData', targetPeriod],
  //   queryFn: getRevenueTargetData
  // });

  // const { } = useQuery({
  //   queryKey: ['getBrokerDashboardCustomerMetrics'],
  //   queryFn: getBrokerDashboardCustomerMetrics
  // });

  const { } = useQuery({
    queryKey: ['getCompanyOnboardingStatus'],
    queryFn: getCompanyOnboardingStatus,
    enabled: !isAdminEmbed
  });

  const { isLoading: isLoadingMetricsSummary } = useQuery({
    queryKey: ['getBrokerDashboardMetricsSummaryData', fieldValue, companyId],
    queryFn: () => getBrokerDashboardMetricsSummaryData(fieldValue, companyId)
  });

  useQuery({
    queryKey: ['getRevenueTrendData', companyId],
    queryFn: () => getRevenueTrendData(companyId)
  });

  useQuery({
    queryKey: ['getInsuranceMetrics', companyId],
    queryFn: () => getInsuranceMetrics(companyId)
  });


  const { isLoading: isLoadingMonthlyMetrics } = useQuery({
    queryKey: ['getDashboardChartMonthlyMetrics', companyId],
    queryFn: () => getDashboardChartMonthlyMetrics(undefined, companyId)
  });

  const { data: expiringPoliciesData, isLoading: isLoadingExpiredPolicies } = useQuery({
    queryKey: ['getAllExpiringPolicies'],
    queryFn: async () => {
      const today = new Date()
      const dayThirty = new Date()
      dayThirty.setDate(today.getDate() + 30)

      const res: any = await getAllExpiringPolicies(0, 30, 'PENDING', undefined, getFormattedDate({ date: dayThirty, toServer: true }), getFormattedDate({ date: today, toServer: true }))
      return res?.expiringPolicies?.content
    }
  });


  // Quick stats for broker dashboard
  const brokerStatsOrigin = [
    {
      title: 'Total Premium YTD',
      value: brokerMetricsSummaryData?.yearToDateSales,
      icon: Users,
      color: 'blue' as const,
      period: fieldValue,
      onClick: () => navigate(`${getDashboardBase()}/policies?type=motor`),
      loading: isLoading,
      className: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-100 dark:border-blue-800/50'
    },
    {
      title: 'Premium This Month',
      value: brokerMetricsSummaryData.monthlySales,
      icon: TrendingUp,
      color: 'green' as const,
      period: getCurrentMonth(),
      onClick: () => navigate(`${getDashboardBase()}/policies?type=motor`),
      loading: isLoading,
      className: 'bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-100 dark:border-green-800/50'
    },
    {
      title: 'Total Commission YTD',
      value: brokerMetricsSummaryData.yearToDateCommission,
      icon: Target,
      color: 'indigo' as const,
      period: fieldValue,
      onClick: () => navigate(`${getDashboardBase()}/commissions`),
      loading: isLoading,
      className: 'bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-900/20 dark:text-indigo-100 dark:border-indigo-800/50'
    },
    {
      title: 'Commission',
      value: brokerMetricsSummaryData.commissionEarned,
      icon: Award,
      color: 'yellow' as const,
      period: getCurrentMonth(),
      onClick: () => navigate(`${getDashboardBase()}/commissions`),
      loading: isLoading,
      className: 'bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-100 dark:border-yellow-800/50'
    }
  ];

  // const brokerStatsCustomer = [
  //   {
  //     title: 'Active Customers',
  //     value: formatNumberValue(customerMetricsData?.retention?.activeCustomers),
  //     change: '',
  //     icon: UserCheck2,
  //     color: 'green' as const,
  //     className: 'bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-100 dark:border-green-800/50'
  //   },
  //   {
  //     title: 'Last Year Customers',
  //     value: formatNumberValue(customerMetricsData?.retention?.customersLastYear),
  //     change: '',
  //     icon: Users,
  //     color: 'blue' as const,
  //     className: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/20 dark:text-blue-100 dark:border-blue-800/80'
  //   },
  //   {
  //     title: 'Retained Customers',
  //     value: formatNumberValue(customerMetricsData?.retention?.retainedCustomers),
  //     change: '',
  //     icon: Repeat,
  //     color: 'yellow' as const,
  //     className: 'bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-100 dark:border-yellow-800/50'
  //   },
  //   {
  //     title: 'Retention Rate',
  //     value: formatPercentageValue(customerMetricsData?.retention?.retentionRate, 2),
  //     change: '',
  //     icon: TrendingUp,
  //     color: 'purple' as const,
  //     className: 'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-900/20 dark:text-purple-100 dark:border-purple-800/50'
  //   }
  // ];

  // Quick stats for broker dashboard

  // const getBrokerStats = () => {
  //   return [
  //     {
  //       title: 'Upcoming',
  //       value: formatNumberValue(insuranceMetrics?.renewalMetrics?.upcomingRenewalsCount),
  //       value2: insuranceMetrics?.renewalMetrics?.upcomingRenewalsPremium,
  //       change: '',
  //       changeType: '',
  //       icon: Clock,
  //       color: 'blue' as const,
  //       className: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-900/40 dark:text-blue-100 dark:border-blue-800/80',
  //       onClick: () => navigate(`${getDashboardBase()}/renewals`)
  //     },
  //     {
  //       title: 'Due For Renewal',
  //       value: formatNumberValue(insuranceMetrics.renewalMetrics.dueForRenewalCount),
  //       value2: insuranceMetrics?.renewalMetrics?.dueForRenewalPremium,
  //       change: '',
  //       changeType: '',
  //       icon: AlertTriangle,
  //       color: 'yellow' as const,
  //       className: 'bg-yellow-50 text-yellow-700 border-yellow-200 dark:bg-yellow-900/20 dark:text-yellow-100 dark:border-yellow-800/50',
  //       onClick: () => navigate(`${getDashboardBase()}/renewals`)
  //     },
  //     {
  //       title: 'Policies Lost',
  //       value: formatNumberValue(insuranceMetrics.renewalMetrics.lostBusinessesCount),
  //       value2: insuranceMetrics.renewalMetrics.lostBusinessesPremium,
  //       changeType:
  //         brokerMetricsSummaryData.commissionChangePercent > 0
  //           ? ChangeType.positive
  //           : ChangeType.negative,
  //       icon: XCircle,
  //       color: 'red' as const,
  //       className: 'bg-red-50 text-red-700 border-red-200 dark:bg-red-900/20 dark:text-red-100 dark:border-red-800/50',
  //       onClick: () => {
  //         navigate(`${getDashboardBase()}/renewals`);
  //       }
  //     },
  //     {
  //       title: 'Policies Renewed',
  //       value: formatNumberValue(insuranceMetrics.renewalMetrics.renewedPoliciesCount),
  //       value2: insuranceMetrics.renewalMetrics.renewedPoliciesPremium,
  //       icon: Users,
  //       color: 'green' as const,
  //       className: 'bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-100 dark:border-green-800/50',
  //       onClick: () => navigate(`${getDashboardBase()}/renewals`)
  //     },
  //   ];
  // };

  const getMetricsCards = () => {
    switch (activeMetricType) {
      // case 'alerts':
      //   return (
      //     isLoading ?
      //       <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6 p-5">
      //         {[1, 2, 3, 4].map((i: number) => <SkeletonLoader key={i} />)}
      //       </div>
      //       :
      //       <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6 p-5">
      //         {/* Stats Grid */}
      //         {getBrokerStats()?.map((stat, index) => (
      //           <div
      //             key={index}
      //             className={`p-3 rounded-2xl border cursor-pointer ${stat.className} dark:bg-darkBg dark:border dark:border-darkBg-border`}
      //             onClick={stat.onClick}>
      //             <div className="flex items-center gap-2 ">
      //               <div className={`rounded-full bg-white bg-opacity-50 dark:bg-transparent`}>
      //                 <stat.icon className="h-5 w-5" />
      //               </div>
      //               <div className='flex flex-col'>
      //                 <div className="flex items-center gap-2">
      //                   <p className="text-sm font-medium">{stat.title}</p>
      //                   <p className="text-lg font-bold">{`${stat.value}`}</p>
      //                 </div>

      //                 <div className="text-md font-bold ">
      //                   {formatNumberValue(stat.value2).toString().includes('k') ||
      //                     formatNumberValue(stat.value2).toString().includes('M')
      //                     ?
      //                     <>
      //                       <TooltipCompent
      //                         placement="bottom"
      //                         bgColor={`bg-gray-600`}
      //                         textColor="text-white"
      //                         message={formatCurrencyAmount(stat.value2, undefined, true)}>
      //                         <div className={`md:text-lg lg:text-md font-bold `}>
      //                           {formatNumberValue(stat.value2)}
      //                         </div>
      //                       </TooltipCompent>
      //                     </>

      //                     : <div className={`md:text-lg lg:text-md font-bold `}>
      //                       {formatNumberValue(stat.value2)}
      //                     </div>
      //                   }

      //                 </div>
      //               </div>


      //             </div>


      //             <p className="text-xs mt-1"></p>
      //           </div>
      //         ))}
      //       </div>
      //   );
      case 'financial':
        return (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6 p-5">
            {brokerStatsOrigin.map((stat, index) => (
              <StatsCard key={index} {...stat} />
            ))}
          </div>
        );
      // case 'customer':
      //   return (
      //     isLoading ?
      //       <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6 p-5">
      //         {[1, 2, 3, 4].map((i: number) => <SkeletonLoader key={i} />)}
      //       </div>
      //       :
      //       <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6 p-5">
      //         {brokerStatsCustomer?.map((stat, index) => (
      //           <div key={index} className={`p-4 rounded-2xl border ${stat.className} `}>
      //             <div className="flex items-center justify-between">
      //               <div className="flex items-center justify-center gap-2">
      //                 <div className={`p-1 rounded-full bg-white bg-opacity-50 dark:bg-transparent`}>
      //                   <stat.icon className="h-5 w-5" />
      //                 </div>
      //                 <div className='flex flex-col'>
      //                   <p className="text-sm font-medium">{stat.title}</p>
      //                   <p className="text-md font-bold ">{stat.value}</p>
      //                 </div>
      //               </div>
      //             </div>


      //             <p className="text-xs mt-1"></p>
      //           </div>
      //         ))}
      //       </div>
      //   );

      default:
        break;
    }
  };

  // const getTargetChart = () => {
  //   return (
  //     <>
  //       {revenueTarget ? (
  //         <div className="h-80 mt-6">
  //           <ResponsiveContainer width="100%" height="100%">
  //             <ComposedChart
  //               data={revenueChartData}
  //               margin={{ top: 10, right: 30, left: 20, bottom: 20 }}>
  //               <CartesianGrid strokeDasharray="3 3" vertical={false} opacity={0.3} />
  //               <XAxis dataKey="month" tick={{ fontSize: 12 }} padding={{ left: 10, right: 10 }} />
  //               <YAxis
  //                 tickFormatter={(value: number) => formatCurrencyAmount(value)}
  //                 tick={{ fontSize: 12 }}
  //                 axisLine={false}
  //                 tickLine={false}
  //               />
  //               <Tooltip
  //                 formatter={(value: number, name: string) => {
  //                   const label = name;
  //                   return [formatCurrencyAmount(value), label];
  //                 }}
  //                 labelStyle={{ fontWeight: 'bold' }}
  //               />
  //               <Legend wrapperStyle={{ paddingTop: 10 }} />
  //               <Bar
  //                 dataKey={'revenue'}
  //                 fill={'#818cf8'}
  //                 name={'Revenue'}
  //                 radius={[4, 4, 0, 0]}
  //                 barSize={30}
  //               />
  //               <Line
  //                 type="monotone"
  //                 dataKey="target"
  //                 name="Target"
  //                 stroke="#f59e0b"
  //                 strokeWidth={2}
  //                 dot={{ r: 0 }}
  //                 activeDot={{ r: 6 }}
  //                 label="Target"
  //               />
  //             </ComposedChart>
  //           </ResponsiveContainer>
  //         </div>
  //       ) : (
  //         <div className="bg-white rounded-2xl shadow-sm border border-gray-200 mt-6 p-6">
  //           <div className="flex justify-between items-center mb-4"></div>
  //           <EmptyState
  //             icon="📊"
  //             title="Target revenue data not available"
  //             description="There is no target revenue data to display currently."
  //           />
  //         </div>
  //       )}
  //     </>
  //   );
  // };

  const priorityName = (priority: string) => {
    if (priority === 'resolved') {
      return 'Renewed';
    }
    else if (priority === 'urgent') {
      return 'Urgent';
    } else if (priority === 'high') {
      return 'At Risk';
    }
    else {
      return null;
    }
  }

  const getTableRowActions = (data: IPolicyRenewal): ITableRowAction[] => {
    const actions = [
      {
        label: 'View',
        icon: <Eye className='h-4 w-4' />,
        callback: (policy: any) => {
          navigate(getPolicyDetailsRoute(policy), { replace: true });
        },
        data: data
      },
    ];

    if (data?.priority && data.priority.toLowerCase() !== 'resolved' && !data.renewed) {
      actions.push(
        {
          label: 'Mark as renewed',
          icon: <CheckCircle2 className="h-4 w-4" />,
          callback: () => markAsRenewed({ customerName: data?.customerName, policyNumber: data?.policyNumber, policyType: data?.policyType, onProceed: inavalidateRenewals }),
          data: data
        }
      )
    }

    if (data.policyType === 'VEHICLE' && !user.isInternalAdmin()) {
      actions.push({
        label: 'Renew policy',
        icon: <ArrowPathIcon className="h-4 w-4" />,
        callback: () => renewMotorPolicy(data),
        data: data
      })
    }

    return actions;
  };

  const columns = useMemo(
    () => [
      {
        Header: 'M',
        columns: [
          {
            Header: 'Policy Number',
            accessor: 'policyNumber',
            Cell: (row: any) => {
              return (
                <div className='flex flex-col w-full items-center'>
                  <Link
                    to={row?.row?.original.policyType === 'VEHICLE'
                      ? `${getDashboardBase()}/policies/${row?.row?.original.policyNumber}?type=motor`
                      : `${getDashboardBase()}/policies/${row?.row?.original.policyNumber}?type=non-motor`
                    }
                  >
                    <span className="hover:underline text-link-main dark:text-darkBg-textLink dark:hover:text-darkBg-textLink">{row.cell.value ?? '-'}</span>
                  </Link>
                  {row?.row?.original.priority && (row?.row?.original.priority?.toLowerCase() === 'urgent' || row?.row?.original.priority?.toLowerCase() === 'high') && (
                    <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${row.row.original.priority.toLowerCase() === 'high'
                      ? 'bg-yellow-200 text-yellow-800' : 'bg-red-200 text-red-800'}`}>
                      {priorityName(row.row.original.priority.toLowerCase())}
                    </div>
                  )}
                </div>
              );
            }
          },
          {
            Header: 'Policy Type',
            accessor: 'policyType',
            Cell: (row: any) => {
              return <PolicyTypesBadge policyType={row.cell.value} />;
            }
          },
          {
            Header: 'Premium',
            accessor: 'premium',
            Cell: (row: any) => {
              return <span>{formatCurrencyAmount(row.cell.value) ?? '-'}</span>;
            }
          },
          {
            Header: 'Customer name',
            accessor: 'customerName',
            Cell: (row: any) => {
              return <span>{row.cell.value ?? '-'}</span>;
            }
          },
          {
            Header: 'Customer phone',
            accessor: 'customerPhone',
            Cell: (row: any) => {
              return <span>{getPhoneNumberFromShortCodeFormat(row.cell.value)}</span>;
            }
          },

          {
            Header: 'Days to expiry',
            accessor: 'daysToExpire',
            Cell: (row: any) => {
              return <span>{row.cell.value ? row.cell.value : '-'}</span>;
            }
          },
          {
            Header: 'Action',
            id: 'Action',
            Cell: (row: any) => {
              return (
                // <GTableActionColumn actions={getTableRowActions(row.row.original)} />

                <div className="cursor-pointer flex justify-center">
                  {getTableRowActions(row.row.original).length > 0 && (
                    <PopOnMe actions={getTableRowActions(row.row.original)}>
                      <EllipsisVerticalIcon className="h-5 w-5" />
                    </PopOnMe>
                  )}
                </div>

              );
            }
          }
        ]
      }
    ],
    []
  );

  const getPolicyDetailsRoute = (policy: IPolicyRenewal) => {

    if (policy.policyType === 'VEHICLE') {
      return `${getDashboardBase()}/policies/${policy.policyNumber}?type=motor`
    }

    return `${getDashboardBase()}/policies/${policy.policyNumber}?type=non-motor`
  }

  const renewalsTbale = () => {
    return (
      <div className="h-full bg-white dark:bg-darkBg rounded-2xl p-6 border dark:border-darkBg-border shadow">
        <div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-darkBg-mainText">Renewals in 30 days</h3>
        </div>

        <GTable
          columns={columns}
          data={expiringPoliciesData?.slice(0, 30) || []}
          showSearch={false}
          loading={isLoadingExpiredPolicies}
          tableType={'upcoming renewal'}
          stickyHeader
          classes="!max-h-[400px]"
        />
      </div>
    );
  };

  return (
    <div className={styles.dashboardContainer}>
      <div>
        <h1 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
          {getGreeting()}, {user?.firstName ? `${user.firstName}` : ''}
        </h1>
        <div className="text-gray-600 dark:text-gray-400 grid grid-cols-2 w-full">
          {"Get a real-time view of your policies and financials and activities. Manage your portfolio, track key metrics, and take action—all from one place"}
        </div>
      </div>

      {/* Layout: Full width by default, sidebar when banner is shown */}
      <div className={shouldShowBannerSidebar ? styles.layoutContainer : styles.layoutContainerWithMinimizedBanner}>
        <div className={`${styles.contentWrapper} ${shouldShowBannerSidebar ? styles.contentWrapperWithBanner : styles.contentWrapperFullWidth}`}>
          <div className={`${styles.bannerSection} dark:bg-darkBg dark:border-darkBg-border`}>
            <div className="p-5 border-b border-gray-100 dark:border-darkBg-border flex md:flex-row flex-col justify-between md:items-center gap-5">

              <div className='flex items-center md:justify-center gap-4'>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-darkBg-mainText">{metricTitle}</h2>
                <div className="w-40">
                  <SelectMenu
                    label=""
                    name="years"
                    listSelectedValue={fieldValue}
                    onChange={(val: string) => {
                      setFieldValue(val)
                    }}
                    list={['2024', '2025', '2026']}
                  />

                </div>
              </div>

              {/* 
          <div className="flex space-x-2 bg-gray-100 dark:bg-gray-700 p-1 rounded-md">
            <button
              className={`px-3 py-1 text-xs font-medium rounded ${activeMetricType === 'financial'
                ? 'bg-white shadow'
                : 'text-gray-500 hover:text-gray-900 dark:text-gray-200'
                }`}
              onClick={() => { setActiveMetricType('financial'); setMetricTitle(' Financial Metrics') }}>
              Financial Metrics {`(${selectedCountryIso})`}
            </button>
            <button
              className={`px-3 py-1 text-xs font-medium rounded ${activeMetricType === 'alerts'
                ? 'bg-white shadow'
                : 'text-gray-500 hover:text-gray-900 dark:text-gray-200'
                }`}
              onClick={() => { setActiveMetricType('alerts'); setMetricTitle(' Renewals') }}>
              Renewals {`(${selectedCountryIso})`}
            </button>
            <button
              className={`px-3 py-1 text-xs font-medium rounded ${activeMetricType === 'customer'
                ? 'bg-white shadow'
                : 'text-gray-500 hover:text-gray-900 dark:text-gray-200'
                }`}
              onClick={() => { setActiveMetricType('customer'); setMetricTitle(' Customer Retention') }}>
              Customer Retention
            </button>
          </div> */}
              {/* <div className="pr-36">
            <Button label='Your NIC Report' className='!bg-blue-700 text-white !w-fit !text-[13px]' />
          </div> */}

            </div>
            {/* Stats Grid */}

            {getMetricsCards()}
          </div>

          {/* Charts Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 ">
            <div className="col-span-2">
              <SalesTrendsChart isLoading={isLoadingMonthlyMetrics} data={monthlyChartMetrics} />
            </div>
            <div className=''>
              <PolicyDistribution />
            </div>
          </div>


          {/* {(user?.isOwner() || user?.isManager() || user?.isSupervisor()) && <BrokerPerformanceTable />}  */}

          {/* Target Achievement Section */}
          <div className="grid grid-cols-1 min-[1024px]:grid-cols-3 gap-6 ">
            {/* <div
          className="bg-white dark:bg-darkBg rounded-2xl shadow-sm border h-full border-gray-200 dark:border-darkBg-border p-6 col-span-1"
          ref={targetAchievementRef}>
          <div className="flex justify-between  mb-4">
            <div className="flex flex-col">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-darkBg-mainText">Target Achievement</h3>
              {revenueTargetData && revenueTargetData?.achievementPercentage > 0 && (
                <span
                  className={`px-3 py-1 w-fit text-xs font-medium rounded-full ${revenueTargetData?.achievementPercentage >= 90
                    ? 'bg-green-100 text-green-800'
                    : revenueTargetData?.achievementPercentage >= 75
                      ? 'bg-yellow-100 text-yellow-800 '
                      : 'bg-red-100 text-red-800'
                    }`}>
                  {revenueTargetData?.achievementPercentage >= 90
                    ? 'Excellent'
                    : revenueTargetData?.achievementPercentage >= 75
                      ? 'Good'
                      : 'Needs improvement'}
                </span>
              )}
            </div>
            <div className="flex  space-x-4">
              <DatePickerInput
                id="month"
                selected={targetPeriod}
                onChange={(date: Date | null) => {
                  if (date) {
                    setTargetPeriod(date);
                  }
                }}
                classes="max-w-[150px] !py-[6px]"
                placeholder="Select month and year"
                showMonthYearPicker
                dateFormat="MMM yyyy"
              />
              <Button
                onClick={handleCreateRevenueTarget}
                label="Set Target"
                bgColor="primary"
                className="text-sm font-medium"
              />
            </div>
          </div>

          <div className="w-full bg-gray-200 rounded-full h-6">
            <div
              className={`h-6 rounded-full text-center relative ${revenueTargetData?.achievementPercentage >= 90
                ? 'bg-green-500'
                : revenueTargetData?.achievementPercentage >= 75
                  ? 'bg-yellow-500'
                  : 'bg-red-500'
                }`}
              style={{ width: `${Math.min(revenueTargetData?.achievementPercentage, 100)}%` }}>
              <div className="flex w-full min-w-[40px] absolute items-center justify-center h-full text-xs font-bold text-white dark:text-gray-800">
                {formatPercentageValue(revenueTargetData?.achievementPercentage, undefined, true)}
              </div>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-3 bg-gray-50 dark:border dark:border-darkBg-border dark:bg-transparent rounded-2xl ">
              <p className="text-sm font-medium text-gray-600 dark:text-darkBg-mainText">{`Current (${selectedCountryIso})`}</p>
              <p className="text-lg font-bold text-gray-900 dark:text-darkBg-mainText">
                {formatCurrencyAmount(revenueTargetData?.currentAmount)}
              </p>
            </div>
            <div className="p-3 bg-gray-50 dark:border dark:border-darkBg-border dark:bg-transparent rounded-2xl ">
              <p className="text-sm font-medium text-gray-600 dark:text-darkBg-mainText">{`Target (${selectedCountryIso})`}</p>
              <p className="text-lg font-bold text-gray-900 dark:text-darkBg-mainText">
                {formatCurrencyAmount(revenueTargetData?.targetAmount)}
              </p>
            </div>
            <div className="p-3 bg-gray-50 dark:border dark:border-darkBg-border dark:bg-transparent rounded-2xl ">
              <p className="text-sm font-medium text-gray-600 dark:text-darkBg-mainText">{`Remaining (${selectedCountryIso})`}</p>
              <p className="text-lg font-bold text-gray-900 dark:text-darkBg-mainText">
                {formatCurrencyAmount(Math.max(0, revenueTargetData?.targetAmount - revenueTargetData?.currentAmount))}
              </p>
            </div>
          </div>

          {!revenueTarget ? <LoadingState /> : getTargetChart()}
        </div> */}

            {/* Expiring policies table */}
            <div className="h-full col-span-2">{renewalsTbale()}</div>

            <RenewalDistributionChart />
          </div>

          {/* Insurance Company Distribution */}
          <div className="w-full grid grid-cols-1 min-[1024px]:grid-cols-3 gap-6">
            <div className="min-[1024px]:col-span-3">
              <InsuranceCompanyDistribution />
            </div>
            <div>
              {/* <NonMotorPolicyDistributionChart /> */}
            </div>
          </div>
        </div>

        {/* Show full banner in sidebar when onboarding incomplete AND not minimized */}
        {isOnboardingIncomplete && (
          <div className={`${styles.bannerContainer} ${isMinimized ? styles.bannerContainerHidden : ''}`}>
            <WorkspaceSetupBanner />
          </div>
        )}
      </div>


      <PolicyModalComponent />
      <ModalComponent />
      <BrokerModal />
    </div>
  );
};

export default BrokerDashboard;
